import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { readDocumentOutput, setDocumentOutput } from '@/lib/work-document-output';
import { ToolResultPageRenderer } from '@/components/tools/ToolResultPageRenderer';
import { WorkActionsProvider } from '@/components/work/WorkActionsContext';
import { buildRunResultTabs, type ToolResultTab } from '@/lib/tool-result-composition';
import { supabase } from '@/lib/supabase';
import { parseResultPayload } from '@shared/types/result-composition';
import { isViewType, type AnyViewEnvelope } from '@shared/types/views';
import { useOptionalAppAssistant } from '@/lib/app-assistant-context';
import { Button } from '@/components/ui/Button';

type FlowResult = { subjectId: string; runId: string; flowId: string | null; tabs: ToolResultTab[]; editable: boolean; discussable: boolean };

export const AccountFlowResult = ({ subjectId, subjectLabel = 'Compte', runId }: { subjectId: string; subjectLabel?: string; runId: string }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const requestedOutput = readDocumentOutput(params);
  const assistant = useOptionalAppAssistant();
  const [result, setResult] = useState<FlowResult | null>(null);
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setResult(null);
    setSelectedTabId(null);
    setLoading(true);
    setError(false);
    void (async () => {
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(subjectId)) return;
      const { data, error: readError } = await supabase.from('flow_executions')
        .select('id,flow_id,subject_id,account_subject_id,status,result_payload,view_payload')
        .eq('id', runId)
        .or(`account_subject_id.eq.${subjectId},and(account_subject_id.is.null,subject_id.eq.${subjectId})`)
        .maybeSingle();
      if (readError) throw readError;
      if (!active) return;
      if (!data || data.status !== 'completed' || (data.account_subject_id ?? data.subject_id) !== subjectId) return;

      const payload = parseResultPayload(data.result_payload);
      if (data.result_payload !== null && !payload) throw new Error('invalid_result_payload');
      const view = data.view_payload as AnyViewEnvelope | null;
      const fallback = view && isViewType(view.view_type) && view.data && typeof view.data === 'object'
        ? { viewType: view.view_type, envelope: view } : null;
      const title = fallback && 'title' in fallback.envelope.data && typeof fallback.envelope.data.title === 'string'
        ? fallback.envelope.data.title : 'Résultat';
      const tabs = buildRunResultTabs({ title, resultPayload: payload, fallback })
        .filter(tab => tab.scope === 'run');
      const onlyTab = tabs.length === 1 ? tabs[0] : null;
      const onlyViewId = onlyTab?.composition.views.length === 1 ? onlyTab.composition.views[0]?.id : null;
      setResult({
        subjectId, runId,
        flowId: data.flow_id,
        tabs,
        discussable: Boolean(payload && tabs.length),
        editable: Boolean(payload) || Boolean(onlyViewId)
          && onlyTab?.viewEnvelopesByInstanceId[onlyViewId ?? '']?.view_type === 'artifact_document',
      });
      setSelectedTabId(tabs[0]?.id ?? null);
    })().catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [subjectId, runId]);

  const currentResult = result?.subjectId === subjectId && result.runId === runId ? result : null;
  const tab = requestedOutput ? currentResult?.tabs.find(candidate => candidate.id === requestedOutput.tabId)
    : currentResult?.tabs.find(candidate => candidate.id === selectedTabId) ?? currentResult?.tabs[0];
  const tabs = currentResult?.tabs ?? [];
  const requestedVersion = params.get('version');
  const applicationParams = new URLSearchParams({ run: runId });
  if (requestedVersion && /^\d{1,10}$/.test(requestedVersion)) applicationParams.set('version', requestedVersion);
  setDocumentOutput(applicationParams, requestedOutput ?? undefined);
  const applicationPath = currentResult?.flowId
    ? `/tools/${encodeURIComponent(currentResult.flowId)}?${applicationParams.toString()}` : null;

  return <section aria-labelledby="account-flow-result-title" className="mt-8 rounded-2xl border border-domino-cardBorder bg-domino-white py-5">
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 md:px-6">
      <h2 id="account-flow-result-title" className="text-lg font-semibold text-domino-ink">Résultat du travail</h2>
      <div className="flex flex-wrap items-center gap-3">
        {currentResult?.discussable && assistant && <Button variant="ghost" onClick={() => assistant.openWork({
          subjectId, subjectLabel, subjectKind: 'company', subjectKey: null,
          threadId: null, topicId: null, topicVersion: null, sourceRunId: null,
          sourceActionId: null, decisionFocus: null, title: 'Questions sur ce résultat', flowResultId: runId,
        })}>Discuter du résultat initial</Button>}
        {tabs.length > 0 && applicationPath && <Link to={applicationPath} className="text-sm font-medium text-domino-aubergine underline underline-offset-4">Ouvrir dans l’application</Link>}
      </div>
    </div>
    {loading ? <p role="status" className="px-5 py-4 text-sm text-domino-inkSoft">Chargement du résultat…</p>
      : error ? <p role="alert" className="px-5 py-4 text-sm text-red-700">Le résultat n’a pas pu être chargé.</p>
      : !tab ? <p className="px-5 py-4 text-sm text-domino-inkSoft">Résultat introuvable ou inaccessible.</p>
      : <>
        {tabs.length > 1 && <nav aria-label="Perspectives du résultat" className="mx-5 mt-4 flex flex-wrap gap-2 border-b border-domino-cardBorder pb-2 md:mx-6">
          {tabs.map(candidate => <button key={candidate.id} type="button" aria-current={candidate.id === tab.id ? 'page' : undefined}
            onClick={() => {
              setSelectedTabId(candidate.id);
              const next = new URLSearchParams(location.search);
              setDocumentOutput(next); next.delete('version');
              navigate({ pathname: location.pathname, search: `?${next.toString()}` }, { replace: true });
            }} className="rounded-lg px-3 py-2 text-sm font-medium text-domino-aubergine hover:bg-domino-muted aria-[current=page]:bg-domino-muted">{candidate.label}</button>)}
        </nav>}
        <WorkActionsProvider actions={[]} historical resultSource={currentResult?.editable ? { kind: 'flow', id: runId } : undefined}>
          <ToolResultPageRenderer composition={tab.composition} viewEnvelopesByInstanceId={tab.viewEnvelopesByInstanceId} documentOutputs={tab.documentOutputs} requestedOutput={requestedOutput} pageMode="artifact" />
        </WorkActionsProvider>
      </>}
  </section>;
};
